import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';
import { H1, Icon, LinkButton } from 'shared/components';
import { buttonVariants } from 'shared/consts';
import FocusLock from 'react-focus-lock';
import {
	OverlayBackdrop,
	CloseButton,
	PopupContent,
} from './StartPageOverlay.styles';
import { useStartPageOverlay } from 'shared/hooks/useStartPageOverlay';

const TITLE_ID = 'start-page-overlay-title';

const StartPageOverlay = ({ PopupSection = [], closeLabel = 'Stäng' }) => {
	const { isVisible, close } = useStartPageOverlay();
	const dialogRef = useRef(null);
	const previousFocusedElementRef = useRef(null);
	const popupDesktopBackgroundImage =
		PopupSection?.DesktiopImage?.Url || PopupSection?.DesktopImage?.Url;
	const popupPhoneBackgroundImage = PopupSection?.PhoneImage?.Url;

	useEffect(() => {
		if (!isVisible) return;

		const root = document.getElementById('__next');
		const { body } = document;
		const previousBodyOverflow = body.style.overflow;
		previousFocusedElementRef.current = document.activeElement;

		body.style.overflow = 'hidden';
		root?.setAttribute('aria-hidden', 'true');
		root?.setAttribute('inert', '');

		const focusDialog = () => {
			dialogRef.current?.focus();
		};

		focusDialog();
		const animationFrameId = window.requestAnimationFrame(focusDialog);

		const handleKeyDown = ({ key }) => {
			if (key === 'Escape') {
				close();
			}
		};

		document.addEventListener('keydown', handleKeyDown);

		return () => {
			window.cancelAnimationFrame(animationFrameId);
			document.removeEventListener('keydown', handleKeyDown);
			body.style.overflow = previousBodyOverflow;
			root?.removeAttribute('aria-hidden');
			root?.removeAttribute('inert');

			if (
				previousFocusedElementRef.current instanceof HTMLElement &&
				document.contains(previousFocusedElementRef.current)
			) {
				previousFocusedElementRef.current.focus();
			}
		};
	}, [close, isVisible]);

	if (!isVisible) return null;

	const hasHeading = Boolean(PopupSection?.Heading);
	const dialogLabelProps = hasHeading
		? { 'aria-labelledby': TITLE_ID }
		: { 'aria-label': 'Popup' };

	return ReactDOM.createPortal(
		<FocusLock group="popupoverlay" returnFocus={false}>
			<OverlayBackdrop
				ref={dialogRef}
				role="dialog"
				aria-modal="true"
				tabIndex={-1}
				{...dialogLabelProps}
			>
				<PopupContent
					$desktopBackgroundImage={popupDesktopBackgroundImage}
					$phoneBackgroundImage={popupPhoneBackgroundImage}
				>
					<div className="start-page-overlay__content">
						<H1
							id={TITLE_ID}
							color="#376CFF"
							word-break={{ base: 'normal', md: 'unset' }}
							font-size={{ base: '60px', md: '6rem' }}
							line-height={{ base: '66px', md: '6rem' }}
						>
							{PopupSection.Heading}
						</H1>

						<LinkButton
							target={PopupSection.Link?.Target}
							href={PopupSection.Link?.Url}
							variant={buttonVariants.Primary}
						>
							{PopupSection.Link?.Name}
						</LinkButton>
					</div>
					<CloseButton
						onClick={close}
						aria-label={closeLabel}
						title={closeLabel}
					>
						<Icon name="close" aria-hidden="true" size="2.9rem" />
					</CloseButton>
				</PopupContent>
			</OverlayBackdrop>
		</FocusLock>,
		document.body,
	);
};

StartPageOverlay.propTypes = {
	closeLabel: PropTypes.string,
};

export default withTheme(StartPageOverlay);
